import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
} from '@mui/material';
import NetworkhealthHandler from 'handlers/network-health.handler';
import { useAppSelector } from 'store/hook';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import useAppTheme from 'hooks/useAppTheme';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import { WarningAmber } from '@mui/icons-material';
import NetworkMesh from './components/NetworkMesh';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// Define the shape of a mesh node including the new fields
interface MeshNode {
  hostname: string;
  mac: string;
  data_type: string; // "node", "connected_device", etc.
  'rssi[dBm]'?: number;
  'last_seen[s]'?: string;
}

// The fetched mesh data
export type MeshData = Record<
  string,
  {
    nodes: MeshNode[];
    connected_devices: MeshNode[];
  }
>;

// The shape of each node passed into MeshStatus
export interface MeshStatusNode {
  id: string; // unique identifier (e.g., mac)
  name: string; // display name (e.g., hostname)
  rssi: number; // numeric RSSI
}

const NetworkHealth: React.FC = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const networkHealthHandler = new NetworkhealthHandler();
  const customerCode = useAppSelector(
    (state) => state.persistedReducer.customer.code
  );

  const [meshData, setMeshData] = useState<MeshData | null>(null);
  const [nodeCount, setNodeCount] = useState<number>(0);
  const [networkStrength, setNetworkStrength] = useState<string>('Bad');
  const [fetchClicked, setFetchClicked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // New state for auto-refresh and refresh interval (in seconds)
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(10);

  // This array is passed to MeshStatus
  const [meshStatusNodes, setMeshStatusNodes] = useState<MeshStatusNode[]>([]);

  // Calculate nodes present: all keys + unique hostnames in nodes and connected_devices.
  function calculateNodeCount(data: MeshData): number {
    const nodeSet = new Set<string>();
    Object.keys(data).forEach((key) => {
      nodeSet.add(key);
      data[key].nodes.forEach((node) => {
        nodeSet.add(node.hostname);
      });
      data[key].connected_devices.forEach((device) => {
        nodeSet.add(device.hostname);
      });
    });
    return nodeSet.size;
  }

  // Calculate average RSSI across all nodes and connected_devices.
  function calculateNetworkStrength(data: MeshData): string {
    let sum = 0;
    let count = 0;
    Object.values(data).forEach((group) => {
      group.nodes.forEach((node) => {
        if (
          node['rssi[dBm]'] !== undefined &&
          typeof node['rssi[dBm]'] === 'number'
        ) {
          sum += node['rssi[dBm]'];
          count++;
        }
      });
      group.connected_devices.forEach((device) => {
        if (
          device['rssi[dBm]'] !== undefined &&
          typeof device['rssi[dBm]'] === 'number'
        ) {
          sum += device['rssi[dBm]'];
          count++;
        }
      });
    });
    if (count === 0) {
      return 'Bad';
    }
    const avg = sum / count;
    if (avg <= 0 && avg >= -60) {
      return 'Excellent';
    } else if (avg < -60 && avg >= -80) {
      return 'Fair';
    } else {
      return 'Bad';
    }
  }

  /**
   * buildMeshStatusNodes:
   *   - Collect all nodes with data_type === "node".
   *   - Group them by `mac`.
   *   - For each group, compute the average RSSI.
   *   - Return a single entry per unique node.
   */
  function buildMeshStatusNodes(data: MeshData): MeshStatusNode[] {
    const nodeMap = new Map<string, { name: string; rssiValues: number[] }>();
    Object.values(data).forEach((group) => {
      group.nodes.forEach((node) => {
        if (
          node.data_type === 'node' &&
          typeof node['rssi[dBm]'] === 'number'
        ) {
          const macKey = node.mac;
          if (!nodeMap.has(macKey)) {
            nodeMap.set(macKey, { name: node.hostname, rssiValues: [] });
          }
          nodeMap.get(macKey)?.rssiValues.push(node['rssi[dBm]']);
        }
      });
    });
    const result: MeshStatusNode[] = [];
    nodeMap.forEach((value, macKey) => {
      const { name, rssiValues } = value;
      const sum = rssiValues.reduce((a, b) => a + b, 0);
      const avg = sum / rssiValues.length;
      result.push({ id: macKey, name, rssi: avg });
    });
    return result;
  }

  async function getNetworkHealthData(customerCode: string) {
    try {
      setIsLoading(true); // Start loading
      const fetchedData = await networkHealthHandler.getAll({ customerCode });
      setMeshData(fetchedData);

      const count = calculateNodeCount(fetchedData);
      setNodeCount(count);

      const strength = calculateNetworkStrength(fetchedData);
      setNetworkStrength(strength);

      const statusNodes = buildMeshStatusNodes(fetchedData);
      setMeshStatusNodes(statusNodes);
    } catch (error) {
      console.error('Error fetching network health data', error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  }

  useEffect(() => {
    if (fetchClicked) {
      void getNetworkHealthData(customerCode).then(() => {
        setFetchClicked(false);
      });
    }
  }, [fetchClicked, customerCode]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      void getNetworkHealthData(customerCode);
      intervalId = setInterval(() => {
        void getNetworkHealthData(customerCode);
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval, customerCode]);

  return (
    <Box
      sx={{
        backgroundColor: palette.pageBackground,
        color: palette.textPrimary,
        minHeight: 'calc(100vh - 148px)',
        height: '100%',
        padding: 2,
        mt: 2,
        borderRadius: '8px',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" gutterBottom>
          {t('network_health')}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <StyledSwitchButton
                checked={autoRefresh}
                onChange={(e) => {
                  setAutoRefresh(e.target.checked);
                }}
              />
            }
            label={t('auto_refresh')}
          />
          {autoRefresh && (
            <FormControl sx={{ minWidth: 140 }} variant="outlined">
              <InputLabel
                sx={{
                  color: palette.textSecondary,
                  backgroundColor: palette.pageBackground,
                  px: 1,
                  '&.Mui-focused': {
                    color: palette.primary,
                  },
                }}
              >
                {t('refresh_interval')}
              </InputLabel>
              <Select
                value={refreshInterval}
                onChange={(e) => {
                  setRefreshInterval(Number(e.target.value));
                  setTimeout(() => {
                    (document.activeElement as HTMLElement)?.blur(); // Correct blur handling
                  }, 0);
                }}
                displayEmpty
                sx={{
                  backgroundColor: palette.boxBackground,
                  color: palette.textPrimary,
                  borderRadius: '6px',
                  border: `1px solid ${palette.borderGrey}`,
                  minWidth: '140px',
                  '& .MuiSvgIcon-root': {
                    color: palette.primary,
                  },
                  '&:hover': {
                    backgroundColor: palette.toolBarBackground,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: palette.menuOptions,
                      color: palette.textPrimary,
                    },
                  },
                }}
              >
                {[5, 10, 15, 30].map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}s
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            disabled={autoRefresh}
            variant="contained"
            color="primary"
            onClick={() => {
              setFetchClicked(true);
            }}
          >
            {t('fetch_data')}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          borderRadius: '8px',
          height: '100%',
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="80vh"
          >
            <SingleFadeLoader />
          </Box>
        ) : meshData ? (
          <>
            <NetworkMesh
              nodeCount={nodeCount}
              count={nodeCount}
              networkStrength={networkStrength}
              meshData={meshData}
              meshStatusNodes={meshStatusNodes}
            />
            {/* <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <NetworkStatus
                  status={nodeCount === 0 ? 'Disconnected' : 'Connected'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AssetsConnected count={nodeCount} />
              </Grid>
              <Grid item xs={12} md={4}>
                <NetworkStrength status={networkStrength} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Card sx={{ backgroundColor: '#112240', p: 2, color: 'white' }}>
                  <Typography variant="h6" gutterBottom>
                    Network Configuration
                  </Typography>
                  <MeshGraph apiResponse={meshData} />
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <MeshStatus nodes={meshStatusNodes} />
              </Grid>
            </Grid> */}
          </>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            gap={2}
            sx={{
              backgroundColor: palette.boxBackground,
              color: palette.textPrimary,
              border: `1px solid ${palette.borderGrey}`,
              borderRadius: '8px',
            }}
          >
            <WarningAmber />
            <Typography>{t('data_not_loaded_yet')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NetworkHealth;
